import { createLogger } from "@/lib/logger";
import type {
  AdaptPhraseResponse,
  ConceptNode,
  GrammarAnalysisResponse,
  GrammarStats,
  RetentionStrength,
  SuggestedQuestion,
  WeakArea,
} from "@/types/branching-canvas";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const logger = createLogger("branching-canvas");

interface Message {
  role: "user" | "assistant";
  content?: string;
  text?: string;
  timestamp?: number;
  firstReceivedTime?: number;
}

interface UseBranchingCanvasOptions {
  messages: Message[];
  targetLanguage: string;
  languageLevel: string;
  enabled?: boolean;
}

/**
 * Calculate retention strength based on time since last practice
 */
function calculateRetention(lastPracticed: number): RetentionStrength {
  const now = Date.now();
  const elapsed = now - lastPracticed;
  const minutes = elapsed / (1000 * 60);

  if (minutes < 30) return "fresh";
  if (minutes < 120) return "decaying";
  return "due";
}

function buildBasicNodes(messages: Message[]): ConceptNode[] {
  const now = Date.now();
  return messages
    .filter((m) => (m.content || m.text || "").trim().length > 0)
    .map((m, idx) => ({
      id: `node-${idx}-${now}`,
      text: m.content || m.text || "",
      translation: "",
      role: m.role,
      timestamp: m.timestamp || m.firstReceivedTime || now,
      grammarConcepts: [],
      vocabulary: [],
      parentId: idx > 0 ? `node-${idx - 1}-${now}` : null,
      branchType: "main" as const,
      complexity: "intermediate" as const,
      wasCorrect: true,
      retentionStrength: "fresh" as const,
      lastPracticed: now,
    }));
}

/**
 * Enhanced Hook to manage the Smart Learning Canvas
 *
 * - Grammar analysis for each phrase
 * - Retention strength tracking
 * - Suggested tutor questions
 * - Weak area detection
 */
export function useBranchingCanvas({
  messages,
  targetLanguage,
  languageLevel,
  enabled = true,
}: UseBranchingCanvasOptions) {
  // Core state
  const [nodes, setNodes] = useState<ConceptNode[]>([]);
  const [grammarStats, setGrammarStats] = useState<GrammarStats[]>([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState<SuggestedQuestion[]>([]);
  const [weakAreas, setWeakAreas] = useState<WeakArea[]>([]);

  // UI state
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [adaptationResult, setAdaptationResult] = useState<AdaptPhraseResponse | null>(null);

  // Track last analyzed message count to trigger on each new message
  const lastAnalyzedCountRef = useRef<number>(0);
  const analysisTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track message count for triggering analysis
  const messageCount = messages.length;

  // Analyze grammar on every complete message (not on content streaming changes)
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Skip if no messages
    if (messageCount === 0) {
      return;
    }

    // Trigger analysis when a new message is added
    const shouldAnalyze = messageCount > lastAnalyzedCountRef.current;

    if (!shouldAnalyze) {
      return;
    }

    // Clear existing timeout
    if (analysisTimeoutRef.current) {
      clearTimeout(analysisTimeoutRef.current);
    }

    // Short delay to ensure message is fully received (not streaming)
    analysisTimeoutRef.current = setTimeout(async () => {
      lastAnalyzedCountRef.current = messageCount;
      setIsAnalyzing(true);

      try {
        const response = await fetch("/api/widgets/analyze-grammar", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: messages.map((m) => ({
              role: m.role,
              content: m.content || m.text || "",
            })),
            targetLanguage,
            languageLevel,
          }),
        });

        if (!response.ok) {
          const errorBody = await response.text().catch(() => "");
          logger.warn("Grammar analysis unavailable", {
            status: response.status,
            errorBody: errorBody.slice(0, 300),
          });
          setNodes(buildBasicNodes(messages));
          setGrammarStats([]);
          setSuggestedQuestions([]);
          setWeakAreas([]);
          return;
        }

        const data = (await response.json()) as GrammarAnalysisResponse;

        // Build nodes from analysis
        const now = Date.now();
        const newNodes: ConceptNode[] = (data.phrases || []).map((phrase, idx) => ({
          id: `node-${idx}-${now}`,
          text: phrase.text,
          translation: phrase.translation || "",
          role: idx % 2 === 0 ? "assistant" : "user", // Alternate for visual variety
          timestamp: now - idx * 60000, // Stagger timestamps for retention demo
          grammarConcepts: phrase.grammarConcepts || [],
          vocabulary: phrase.vocabulary || [],
          parentId: idx > 0 ? `node-${idx - 1}-${now}` : null,
          branchType: "main" as const,
          complexity: "intermediate" as const,
          wasCorrect: phrase.wasCorrect !== false,
          retentionStrength: calculateRetention(now - idx * 60000),
          lastPracticed: now - idx * 60000,
        }));

        setNodes(newNodes);
        setGrammarStats(data.grammarStats || []);
        setSuggestedQuestions(data.suggestedQuestions || []);
        setWeakAreas(data.weakAreas || []);
      } catch (error) {
        logger.warn("Grammar analysis unavailable", {
          error: error instanceof Error ? error.message : String(error),
        });
        // Create basic nodes without grammar analysis on error
        setNodes(buildBasicNodes(messages));
        setGrammarStats([]);
        setSuggestedQuestions([]);
        setWeakAreas([]);
      } finally {
        setIsAnalyzing(false);
      }
    }, 500); // Short 500ms delay to ensure message is complete

    return () => {
      if (analysisTimeoutRef.current) {
        clearTimeout(analysisTimeoutRef.current);
      }
    };
  }, [messageCount, messages, targetLanguage, languageLevel, enabled]);

  // Update retention strength periodically
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const interval = setInterval(() => {
      setNodes((prev) =>
        prev.map((node) => ({
          ...node,
          retentionStrength: calculateRetention(node.lastPracticed),
        }))
      );
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [enabled]);

  // Toggle expanded view
  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Handle node selection
  const handleNodeSelect = useCallback(
    (nodeId: string) => {
      setSelectedNodeId(nodeId === selectedNodeId ? null : nodeId);
    },
    [selectedNodeId]
  );

  // Adapt phrase API call
  const handleAdaptPhrase = useCallback(
    async (node: ConceptNode) => {
      if (!node) return null;

      setIsLoading(true);
      try {
        const response = await fetch("/api/widgets/adapt-phrase", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            originalPhrase: node.text,
            originalContext: "previous conversation turn",
            newContext: "current conversation moment",
            targetLanguage,
            languageLevel,
          }),
        });

        if (!response.ok) throw new Error("Failed to adapt phrase");

        const result = (await response.json()) as AdaptPhraseResponse;
        setAdaptationResult(result);
        return result;
      } catch (error) {
        logger.error("Adapt phrase error", {
          error: error instanceof Error ? error.message : String(error),
        });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [targetLanguage, languageLevel]
  );

  // Clear adaptation result
  const clearAdaptationResult = useCallback(() => {
    setAdaptationResult(null);
  }, []);

  // Recent nodes for mini-view (last 8)
  const recentNodes = useMemo(() => nodes.slice(-8), [nodes]);

  // Nodes due for review
  const dueNodes = useMemo(() => nodes.filter((n) => n.retentionStrength === "due"), [nodes]);

  return {
    // Data
    nodes,
    recentNodes,
    dueNodes,
    grammarStats,
    suggestedQuestions,
    weakAreas,
    messageCount,

    // State
    selectedNodeId,
    isExpanded,
    isLoading: isLoading || isAnalyzing,
    adaptationResult,

    // Actions
    toggleExpand,
    handleNodeSelect,
    handleAdaptPhrase,
    clearAdaptationResult,
  };
}
